import React, {useContext, useEffect, useState} from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Stack,
  CircularProgress
} from '@mui/material';
import { TNtfyNew } from '../../_types/ntfy';
import { useDispatch } from 'react-redux';
import { NtfyReport } from '../../features/store/ntfy.slice';
import { IUser } from "../../_types/user";
import { FetchContext } from "../../features/contexts/fetch";
import {
  PermissionToggleList,
  PermissionSelection, AvailablePermissionSet, PermissionItem,
} from '../PermissionToggleList/PermissionToogleList';

export function findUnselectedPermissions(
  available: AvailablePermissionSet[],
  selected: Record<string, string[]>
): Record<string, string[]> {
  const result: Record<string, string[]> = {};

  for (const { identifier, availablePermissions } of available) {
    const selectedForApi = new Set(selected[identifier] ?? []);

    const unselected = availablePermissions
      .filter(perm => !selectedForApi.has(perm.value))
      .map(perm => perm.value); // only return the values

    if (unselected.length > 0) {
      result[identifier] = unselected;
    }
  }

  return result;
}

function removeEmptyArrays(obj: Record<string, string[]>): Record<string, string[]> {
  return Object.fromEntries(
    Object.entries(obj).filter(([_, arr]) => arr.length > 0)
  );
}


export type AssignUserPemisssionsDialogProps = {
  user: IUser | null,
  open: boolean;
  loading?: boolean;
  onClose: () => void;
  onSave: (user: IUser | null, selected: PermissionSelection, unselected: PermissionSelection) => void | Promise<void>;
};

export function AssignUserPermissionsDialog({
                                        user,
                                        open,
                                        loading = false,
                                        onClose,
                                        onSave,
                                      }: AssignUserPemisssionsDialogProps) {
  const [busy, setBusy] = useState(false);
  const [allPermissions, setAllPermissions] = useState<AvailablePermissionSet[] | []>([]);
  const [selected, setSelected] = useState<PermissionSelection | {}>({});
  const dispatch = useDispatch<any>();

  const fetchContext = useContext(FetchContext);

  useEffect(() => {
    if (open) {
      void (async () => {
        setBusy(true);
        setSelected({});

        let selectedPermissions = [];

        try {
          // @ts-ignore
          const res = await fetchContext.authAxios.get(
            `/users/${user?.user_id ?? ''}/permissions`,
          );

          selectedPermissions = res.data;
        } catch (err: any) {
          const notification = TNtfyNew(
            err.message,
            'error',
            'long',
          );

          dispatch(NtfyReport(notification));
        }

        try {
          // @ts-ignore
          const {data} = await fetchContext.authAxios.get(
            '/permissions',
          );

          setSelected(selectedPermissions);
          setAllPermissions(data);
        } catch (err: any) {
          const notification = TNtfyNew(
            err.message,
            'error',
            'long',
          );

          dispatch(NtfyReport(notification));
        }

        setBusy(false);
      })();
    }
  }, [open]);



  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();

    const unselected = removeEmptyArrays(findUnselectedPermissions(allPermissions, selected));
    const cleanSelected = removeEmptyArrays(selected);
    await onSave(user, cleanSelected, unselected);
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="sm">
      <form onSubmit={handleSubmit}>
        <DialogTitle>Permissions for {user?.name ?? ''}</DialogTitle>
        <DialogContent>
          <PermissionToggleList
            data={allPermissions}
            value={selected}
            onChange={setSelected}
            title="Assign Permissions"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            color="error"
            disabled={loading || busy}
            startIcon={loading || busy? <CircularProgress size={18} /> : undefined}
          >
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
